import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Radio,
  Wifi,
  WifiOff,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  HelpCircle,
  RefreshCw,
  Play,
  Signal,
  Router,
} from 'lucide-react'
import { getDashboardStats, syncAll } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import { useRole } from '../lib/RoleContext'
import { hasRole } from '../lib/role'

interface StatCardProps {
  title: string
  value: number
  icon: React.ElementType
  color: string
  bgColor: string
}

function StatCard({ title, value, icon: Icon, color, bgColor }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${bgColor}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const role = useRole()
  const queryClient = useQueryClient()

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const response = await getDashboardStats()
      return response.data
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const syncMutation = useMutation({
    mutationFn: (dryRun: boolean) => syncAll(dryRun),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboardStats'] })
      queryClient.invalidateQueries({ queryKey: ['radios'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load dashboard stats</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {stats?.last_sync
              ? `Last sync: ${formatDistanceToNow(new Date(stats.last_sync))} ago`
              : 'No sync performed yet'}
          </p>
        </div>
        {hasRole(role, 'admin') && (
          <div className="flex space-x-3">
            <button
              onClick={() => syncMutation.mutate(true)}
              disabled={syncMutation.isPending}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <Play className="h-4 w-4 mr-2" />
              Dry Run
            </button>
            <button
              onClick={() => syncMutation.mutate(false)}
              disabled={syncMutation.isPending}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`}
              />
              Sync All
            </button>
          </div>
        )}
      </div>

      {/* Customers Down alert — active customers with no online radio */}
      {(stats?.customers_down ?? 0) > 0 && (
        <Link
          to="/customers-down"
          className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg p-4 hover:bg-red-100 transition-colors"
        >
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 mr-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">Customers Down</p>
              <p className="text-xs text-red-600">
                Active customers offline more than a week with no connected radio — likely outages
              </p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-red-700">{stats?.customers_down}</span>
            <p className="text-xs text-red-600">view details →</p>
          </div>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Radios"
          value={stats?.total_radios ?? 0}
          icon={Radio}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="Base Nodes"
          value={stats?.bn_count ?? 0}
          icon={Signal}
          color="text-orange-600"
          bgColor="bg-orange-100"
        />
        <StatCard
          title="Remote Nodes"
          value={stats?.rn_count ?? 0}
          icon={Router}
          color="text-sky-600"
          bgColor="bg-sky-100"
        />
        <StatCard
          title="Connected"
          value={stats?.connected_radios ?? 0}
          icon={Wifi}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="Disconnected"
          value={stats?.disconnected_radios ?? 0}
          icon={WifiOff}
          color="text-gray-600"
          bgColor="bg-gray-100"
        />
        <StatCard
          title="Rogue Devices"
          value={stats?.rogue ?? 0}
          icon={HelpCircle}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
      </div>

      {/* Splynx Sync Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Splynx Sync Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-700">{stats?.synced ?? 0}</p>
              <p className="text-sm text-green-600">Matched</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
            <HelpCircle className="h-8 w-8 text-gray-500" />
            <div>
              <p className="text-2xl font-bold text-gray-700">{stats?.unmatched ?? 0}</p>
              <p className="text-sm text-gray-500">Unmatched</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-700">{stats?.pending ?? 0}</p>
              <p className="text-sm text-yellow-600">Pending</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-2xl font-bold text-red-700">{stats?.error ?? 0}</p>
              <p className="text-sm text-red-600">Errors</p>
            </div>
          </div>
        </div>
      </div>

      {/* API Quota Status */}
      {stats?.api_quota && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tarana API Quota Status</h2>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">
                  {stats.api_quota.calls_used.toLocaleString()} / {stats.api_quota.calls_limit.toLocaleString()} calls used
                </span>
                <span className={`font-semibold ${
                  stats.api_quota.percentage_used >= 80 ? 'text-red-600' :
                  stats.api_quota.percentage_used >= 50 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {stats.api_quota.percentage_used}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${
                    stats.api_quota.percentage_used >= 80 ? 'bg-red-600' :
                    stats.api_quota.percentage_used >= 50 ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(stats.api_quota.percentage_used, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{stats.api_quota.calls_remaining.toLocaleString()}</p>
                <p className="text-xs text-gray-600 mt-1">Remaining</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-700">{stats.api_quota.month_year}</p>
                <p className="text-xs text-blue-600 mt-1">Month</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">10,000</p>
                <p className="text-xs text-gray-600 mt-1">Monthly Limit</p>
              </div>
              <div className={`p-3 rounded-lg ${stats.api_quota.is_disabled ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className={`text-2xl font-bold ${stats.api_quota.is_disabled ? 'text-red-700' : 'text-green-700'}`}>
                  {stats.api_quota.is_disabled ? 'DISABLED' : 'ACTIVE'}
                </p>
                <p className={`text-xs mt-1 ${stats.api_quota.is_disabled ? 'text-red-600' : 'text-green-600'}`}>
                  API Status
                </p>
              </div>
            </div>

            {/* Warning/Error Messages */}
            {stats.api_quota.is_disabled && stats.api_quota.disable_reason && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  <strong>API Disabled:</strong> {stats.api_quota.disable_reason}
                </p>
              </div>
            )}
            {!stats.api_quota.is_disabled && stats.api_quota.percentage_used >= 80 && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> You've used {stats.api_quota.percentage_used}% of your monthly API quota.
                  Consider reducing API calls or waiting until next month.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Result */}
      {syncMutation.data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {syncMutation.data.data.dry_run ? 'Dry Run' : 'Sync'} Results
          </h2>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {syncMutation.data.data.total}
              </p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {syncMutation.data.data.success}
              </p>
              <p className="text-sm text-gray-600">Success</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {syncMutation.data.data.failed}
              </p>
              <p className="text-sm text-gray-600">Failed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-500">
                {syncMutation.data.data.skipped}
              </p>
              <p className="text-sm text-gray-600">Skipped</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
